"use client";

import React from "react";
import { motion } from "framer-motion";
import { Image } from "@imagekit/next";
import { useRouter } from "next/navigation";
import {
  UsersIcon,
  SparklesIcon,
  ShieldCheckIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

const promises = [
  {
    title: "The People",
    preText:
      "We don’t just fill seats – you’ll travel with driven doers, kind creators, and clarity-seeking minds who feel like fuel, not noise. ",
    boldText: "Curate tribes.",
    icon: UsersIcon,
  },
  {
    title: "The Experience",
    preText: "Expect more than sightseeing. Every trip is a ",
    boldText: "blend of learning, growth, and soul.",
    icon: SparklesIcon,
  },
  {
    title: "The Ease & Safety",
    preText:
      "With flexible plans and our on-ground team, we’ve got your back – from airport pickup to your last coffee stop. ",
    boldText: "Book confidently.",
    icon: ShieldCheckIcon,
  },
  {
    title: "The Curation",
    preText:
      "We design the unexpected. Hidden gems, thoughtful details, and authentic local moments – ",
    boldText: "crafted for outcomes, not algorithms.",
    icon: MagnifyingGlassIcon,
  },
];

const textVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.2,
      duration: 0.6,
      ease: "easeOut",
    },
  }),
};

const folder = process.env.NEXT_PUBLIC_IMAGEKIT_FOLDER || "";

const OurPromise = () => {
  const router = useRouter();
  return (
    <section className="bg-white py-12 px-4 sm:px-6 md:px-12 lg:px-20">
      <div className="mx-auto flex flex-col md:flex-row gap-12 md:items-center max-w-7xl">
        {/* Left - Text Content */}
        <div className="w-full md:w-1/2">
          <motion.h2
            className="text-[#FFB61A] font-bricolage text-3xl sm:text-4xl md:text-5xl font-black mb-6 sm:mb-8 leading-tight"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            Our Promise:
            <span className="text-[#1d1d1d] font-semibold">
              {" "}
              Purpose, People & Peace{" "}
              <span className="text-[#FFB61A]">of Mind</span>
            </span>
          </motion.h2>

          <div className="space-y-8">
            {promises.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="border-b-4 border-transparent hover:border-[#FFB61A] transition-all duration-300 pb-4"
                  custom={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={textVariant}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <IconComponent className="h-6 w-6 sm:h-8 sm:w-8 text-[#FFB61A]" />
                    <h3 className="text-xl sm:text-2xl font-bold text-[#1d1d1d]">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-[#1d1d1d] text-base sm:text-lg leading-relaxed">
                    {item.preText}
                    <strong>{item.boldText}</strong>
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Right - Image */}
        <motion.div
          className="w-full md:w-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <div className="w-full h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] rounded-lg overflow-hidden shadow-lg relative">
            <Image
              src={`${folder}/inclusion.jpg`}
              alt="Outcome & Outing adventure"
              fill
              className="object-cover object-center"
              priority
            />
          </div>
        </motion.div>
      </div>
      <motion.div
        className="text-center mt-6 w-full"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.5 }}
        viewport={{ once: true }}
      >
        <motion.button
         onClick={() =>
                    window.open("https://invite.outcomeouting.com/founders", "_blank")
                  }
          className="relative bg-[#FFAE00] hover:bg-[#063258] inline-flex items-center justify-center px-6 py-3 sm:px-8 sm:py-3.5 rounded-full border-2 border-[#FFAE00] text-white font-bold transition-all duration-300 hover:text-white hover:shadow-lg overflow-hidden group text-sm sm:text-base"
          whileHover={{
            scale: 1.05,
            boxShadow: "0 20px 40px rgba(255, 174, 0, 0.3)",
          }}
          whileTap={{ scale: 0.95 }}
        >
          <span className="relative z-10 flex items-center">
            Request Invite
            <motion.span
              className="ml-3"
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              →
            </motion.span>
          </span>
          {/* Background hover effect */}
          <span className="absolute inset-0 z-0 bg-[#063258] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
        </motion.button>
      </motion.div>
    </section>
  );
};

export default OurPromise;
